import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EmployeeList from './pages/EmployeeList';
import EmployeeProfile from './pages/EmployeeProfile';
import Settings from './pages/Settings';
import LeavePolicies from './pages/LeavePolicies';
import HolidayAdmin from './pages/HolidayAdmin';
import IncidentAdmin from './pages/IncidentAdmin';
import MyIncidents from './pages/MyIncidents';
import Scheduling from './pages/Scheduling';
import Attendance from './pages/Attendance';
import FaceSyncStatus from './pages/FaceSyncStatus';
import TaskAdmin from './pages/TaskAdmin';
import MyAccount from './pages/MyAccount';
import JobMonitor from './pages/JobMonitor';
import Timecards from './pages/Timecards';
import PlanVsActual from './pages/PlanVsActual';
import AuditLogsReport from './pages/AuditLogsReport';
import Reports from './pages/Reports';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
            <Layout>
              <EmployeeList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <EmployeeProfile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave-policies"
        element={
          <ProtectedRoute>
            <Layout>
              <LeavePolicies />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/holiday-admin"
        element={
          <ProtectedRoute>
            <Layout>
              <HolidayAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/incident-admin"
        element={
          <ProtectedRoute>
            <Layout>
              <IncidentAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-incidents"
        element={
          <ProtectedRoute>
            <Layout>
              <MyIncidents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduling"
        element={
          <ProtectedRoute>
            <Layout>
              <Scheduling />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <Layout>
              <Attendance />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/face-sync-status"
        element={
          <ProtectedRoute>
            <Layout>
              <FaceSyncStatus />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/task-admin"
        element={
          <ProtectedRoute>
            <Layout>
              <TaskAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-account"
        element={
          <ProtectedRoute>
            <Layout>
              <MyAccount />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-monitor"
        element={
          <ProtectedRoute>
            <Layout>
              <JobMonitor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/timecards"
        element={
          <ProtectedRoute>
            <Layout>
              <Timecards />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/plan-vs-actual"
        element={
          <ProtectedRoute>
            <Layout>
              <PlanVsActual />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/audit-logs"
        element={
          <ProtectedRoute>
            <Layout>
              <AuditLogsReport />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
